import { ThemeProvider } from '@mui/material/styles';
import { AppManagerProvider } from 'apps/AppManagerProvider';
import { AuthContextProvider } from 'components/contexts/AuthCtx';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { ApolloCoreProvider } from '_apollo/ApolloProvider';
import { apolloClient } from '_apollo/config';
// import ScrollToTop from 'components/atoms/ScrollToTop/ScrollToTop';
import { createBrowserHistory } from 'history';
import { ConfirmProvider } from 'material-ui-confirm';

import { createTheme } from '@mui/material/styles';
import { Router } from 'react-router';
import webErrorLink from 'services/apollo/errorLink';
import { firebaseAppAuth } from 'services/firebase';
import 'services/gtm';
// import { HotjarProvider } from 'services/hj/index';
import 'services/sentry';
import 'utils/localizations/i18next';
import './index.css';
import * as serviceWorker from './serviceWorker';
import ErrorBoundary, { reloadForStaleChunk } from 'utils/ErrorBoundary';
import { ConnectionStatusNotifier } from 'components/atoms/ConnectionStatusNotifier';

// Retry after a render error from an empty cache, so data that broke the
// render is fetched again instead of being rendered again as is.
const clearApolloStore = () => apolloClient?.clearStore();

// Vite fires this when a lazy chunk (or its CSS) of the previous deploy cannot
// be loaded. Reloading picks up the current index.html; without it the import
// rejects inside React and the ErrorBoundary has to catch it.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForStaleChunk()) event.preventDefault();
});

const Root: React.FC = () => {
  React.useEffect(() => {
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles?.parentElement?.removeChild(jssStyles);
    }
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary onRetry={clearApolloStore}>
        <ThemeProvider theme={createTheme()}>
          <Router history={createBrowserHistory()}>
            <ApolloCoreProvider
              uri={process.env['REACT_APP_APOLLO_URI'] as string}
              uriWs={process.env['REACT_APP_APOLLO_WS_URI'] as string}
              firebaseAppAuth={firebaseAppAuth}
              errorCallback={webErrorLink}
            >
              {/* <ScrollToTop> */}
              {/* <I18NUrlProvider> */}
              <AuthContextProvider>
                <ConfirmProvider>
                  <ConnectionStatusNotifier />
                  <AppManagerProvider />
                </ConfirmProvider>
              </AuthContextProvider>
              {/* </I18NUrlProvider> */}
              {/* </ScrollToTop> */}
            </ApolloCoreProvider>
          </Router>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
};

const container = document.getElementById('root');
// React 18: a single root is created once and reused across HMR updates.
// The prod HTML ships an empty `<div id="root">` (no real SSR markup — the
// server only serves static files, prerender-node handles crawlers), so
// createRoot().render() is correct in every case; hydrateRoot would only
// apply if the markup were server-rendered.
const root = createRoot(container as HTMLElement);

const renderApp = () => {
  root.render(<Root />);
};

// Webpack/CRA dev HMR. `typeof module` is guarded so this is a no-op (not a
// ReferenceError) under Vite's ESM runtime, where React Fast Refresh is
// provided by @vitejs/plugin-react instead.
declare const module: any;
if (
  process.env.NODE_ENV !== 'production' &&
  typeof module !== 'undefined' &&
  module.hot
) {
  module.hot.accept('.', renderApp);
}

renderApp();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
serviceWorker.register();
